// components/BarcodeScanner.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, Card, CardContent, Typography, LinearProgress, Button } from "@mui/material";
import ScannerIcon from '@mui/icons-material/Scanner';

export default function BarcodeScanner({ onDetected, onError, active = true }) {
  const hasEmittedRef = useRef(false);
  
  // --- LOGIC FOR PHYSICAL SCANNER ---
  const scannerBuffer = useRef("");
  const lastKeyTime = useRef(0);

  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Shared success handler for Physical Scanner
    const handleScanSuccess = (text) => {
      if (hasEmittedRef.current) return;
      hasEmittedRef.current = true;
      
      setResult(text);
      setLoading(false);
      onDetected?.(text);
    };

    // --- PHYSICAL SCANNER KEYBOARD LISTENER ---
    const handleKeyDown = (e) => {
      // Don't process if inactive or already detected a code
      if (!active || hasEmittedRef.current) return;

      const currentTime = Date.now();
      const diff = currentTime - lastKeyTime.current;
      
      // Hardware scanners typically send characters < 50ms apart.
      // If the gap is larger, it's likely a human typing; reset the buffer.
      if (diff > 50) {
        scannerBuffer.current = "";
      }

      // Most scanners send an 'Enter' key at the end of a successful scan
      if (e.key === "Enter") {
        if (scannerBuffer.current.length > 2) {
          setLoading(true);
          handleScanSuccess(scannerBuffer.current);
          scannerBuffer.current = "";
        }
      } else if (e.key.length === 1) {
        // Append characters to buffer
        scannerBuffer.current += e.key;
      }
      
      lastKeyTime.current = currentTime;
    };

    if (!active) {
      return;
    }

    // Reset states when component becomes active
    hasEmittedRef.current = false;
    setResult("");
    setLoading(false);

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [active, onDetected]);

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Card sx={{ width: "100%", maxWidth: 500, borderRadius: 3, boxShadow: 4 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Physical Barcode Scanner
          </Typography>

          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          <Box sx={{ 
            p: 4, 
            textAlign: "center", 
            bgcolor: "#f5f5f5", 
            borderRadius: 2, 
            border: "2px dashed #ccc",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1
          }}>
            {!result ? (
              <>
                <ScannerIcon sx={{ fontSize: 60, color: active ? "primary.main" : "disabled" }} />
                <Typography variant="body1" color="text.secondary">
                  {active ? "Scanner Ready. Please scan a barcode." : "Scanner Inactive"}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  (Uses Keyboard Emulation Mode)
                </Typography>
              </>
            ) : (
              <Box sx={{ width: "100%" }}>
                <Typography variant="overline" sx={{ color: "success.main", fontWeight: "bold" }}>
                  Scan Complete
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    p: 2, 
                    my: 1,
                    bgcolor: "#e8f5e9", 
                    color: "#2e7d32", 
                    borderRadius: 2, 
                    fontWeight: "bold",
                    wordBreak: "break-all"
                  }}
                >
                  {result}
                </Typography>
                <Button 
                  fullWidth 
                  variant="contained" 
                  sx={{ mt: 1 }} 
                  onClick={() => {
                    setResult("");
                    hasEmittedRef.current = false;
                  }}
                >
                  Scan Another Item
                </Button>
              </Box>
            )}
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, textAlign: "center", color: "text.secondary" }}>
            Ensure the application window is focused while scanning.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}